'use client';

import { useState } from 'react';

import { CalendarRange, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { formatLocaleDate } from '@kit/shared/utils';
import { Badge } from '@kit/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@kit/ui/card';

import { SeasonPriceBlock } from '~/components/class-seasons/season-price-block';
import { getSeasonUnavailableReason } from '~/lib/class-seasons/class-season-quote';

import {
  ClassSeasonFlow,
  type ClassSeasonFlowProps,
  describeClassSeason,
  parseSeasonDate,
} from '../../_components/class-season-flow';
import { ClassFilters, NoMatchingClasses } from '../../_components/class-filters';
import { getSeasonScheduleChoices } from '../../_lib/class-season-catalog';
import {
  type ClassFilterState,
  EMPTY_CLASS_FILTERS,
  matchesClassFilters,
} from '../../_lib/class-filters';
import type { ClassSeasonDetails } from '../../_lib/server/class-season.loader';
import { SeasonScheduleCatalog } from './season-schedule-catalog';

type SeasonListSectionProps = Omit<
  ClassSeasonFlowProps,
  'season' | 'initialStep' | 'onDone' | 'onWalletPaid'
> & {
  seasons: ClassSeasonDetails[];
  /** Deep link (?season=<schedule id>): open that season's drawer. */
  initialSeasonId?: string;
  onWalletPaid?: ClassSeasonFlowProps['onWalletPaid'];
  layout?: 'cards' | 'schedule';
  locationImages?: Record<string, string[]>;
};

/**
 * Open class seasons on /book/packages: one card per season schedule; a card
 * opens the same season drawer and checkout as a season class on /book.
 */
export function SeasonListSection({
  seasons,
  initialSeasonId,
  wallet,
  onWalletPaid,
  layout = 'cards',
  locationImages,
  ...flowProps
}: SeasonListSectionProps) {
  const { t, i18n } = useTranslation('bookings');
  const [state, setState] = useState(() => ({
    selectedId: seasons.some(
      (season) => season.classScheduleId === initialSeasonId,
    )
      ? (initialSeasonId ?? null)
      : null,
    walletBalance: null as number | null,
    filters: EMPTY_CLASS_FILTERS,
  }));

  if (seasons.length === 0) return null;

  const selected = seasons.find(
    (season) => season.classScheduleId === state.selectedId,
  );
  const formatDate = (date: string) =>
    formatLocaleDate(parseSeasonDate(date), i18n.language, {
      month: 'short',
      day: 'numeric',
    });

  return (
    <section className="space-y-4" data-test="class-seasons">
      <h2 className="text-xl font-semibold">{t(layout === 'schedule' ? 'selectClass' : 'season.sectionTitle')}</h2>

      {layout === 'schedule' ? (
        <FilteredSeasonScheduleCatalog
          seasons={seasons}
          filters={state.filters}
          onFiltersChange={(filters) => setState((previous) => ({ ...previous, filters }))}
          locationImages={locationImages}
          onSelect={(selectedId) => setState((previous) => ({ ...previous, selectedId }))}
        />
      ) : (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {seasons.map((season) => {
          const { seasonName, subtitle } = describeClassSeason(
            season,
            i18n.language,
          );
          const isFull = getSeasonUnavailableReason(season.quote) === 'full';

          return (
            <Card
              key={season.classScheduleId}
              role="button"
              tabIndex={0}
              data-test="class-season-card"
              className="hover:border-primary cursor-pointer transition-colors"
              onClick={() =>
                setState((prev) => ({
                  ...prev,
                  selectedId: season.classScheduleId,
                }))
              }
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setState((prev) => ({
                    ...prev,
                    selectedId: season.classScheduleId,
                  }));
                }
              }}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-lg">{seasonName}</CardTitle>
                  {isFull ? (
                    <Badge variant="warning">{t('season.full')}</Badge>
                  ) : null}
                </div>
                {subtitle ? (
                  <p className="text-muted-foreground flex items-center gap-1.5 text-sm">
                    <Users className="h-3.5 w-3.5 shrink-0" />
                    {subtitle}
                  </p>
                ) : null}
                <p className="text-muted-foreground flex items-center gap-1.5 text-sm">
                  <CalendarRange className="h-3.5 w-3.5 shrink-0" />
                  {formatDate(season.quote.starts_on)} –{' '}
                  {formatDate(season.quote.ends_on)}
                </p>
              </CardHeader>
              <CardContent>
                <SeasonPriceBlock quote={season.quote} />
              </CardContent>
            </Card>
          );
        })}
      </div>
      )}

      {selected ? (
        <ClassSeasonFlow
          key={selected.classScheduleId}
          season={selected}
          onDone={() => setState((prev) => ({ ...prev, selectedId: null }))}
          onWalletPaid={(walletBalance) => {
            setState((prev) => ({ ...prev, walletBalance }));
            onWalletPaid?.(walletBalance);
          }}
          wallet={{
            enabled: wallet.enabled,
            balance: onWalletPaid ? wallet.balance : (state.walletBalance ?? wallet.balance),
          }}
          {...flowProps}
        />
      ) : null}
    </section>
  );
}

function FilteredSeasonScheduleCatalog({
  seasons,
  filters,
  onFiltersChange,
  ...catalogProps
}: Parameters<typeof SeasonScheduleCatalog>[0] & {
  filters: ClassFilterState;
  onFiltersChange: (filters: ClassFilterState) => void;
}) {
  const filterable = seasons.map((season) => ({
    season,
    slots: getSeasonScheduleChoices(season),
    eligibility: season.eligibility,
  }));
  const visibleSeasons = filterable
    .filter((entry) => matchesClassFilters(entry, filters))
    .map((entry) => entry.season);

  return (
    <>
      <ClassFilters classes={filterable} value={filters} onChange={onFiltersChange} />
      {visibleSeasons.length > 0 ? (
        <SeasonScheduleCatalog seasons={visibleSeasons} {...catalogProps} />
      ) : (
        <NoMatchingClasses onClear={() => onFiltersChange(EMPTY_CLASS_FILTERS)} />
      )}
    </>
  );
}
